import { memo } from 'react'
import { InventoryProductCard } from './InventoryProductCard'
import { normalizeDisplayCardName } from './inventoryDisplayUtils'
import type { InventoryProduct } from './inventoryTypes'
import filterStyles from '../../../components/search/FilterBar.module.css'

/** Row actions, keyed by product — one stable object for the whole list (see useStableHandlers). */
export type InventoryRowActions = {
  toggleSelect: (productId: string) => void
  openImage: (url: string, alt: string) => void
  edit: (product: InventoryProduct) => void
  setConsignment: (product: InventoryProduct) => void
  priceHistory: (product: InventoryProduct, displayName: string) => void
  removeSale: (productId: string, displayName: string) => void
  removeManual: (productId: string, displayName: string) => void
  report: (product: InventoryProduct, displayName: string, effectivePriceCents: number | null) => void
  starterLockedSale: () => void
  starterLockedManual: () => void
}

export type InventoryProductRowProps = {
  product: InventoryProduct
  index: number
  density: 'comfortable' | 'compact'
  /** The Recent sales window, when the list has one. */
  soldWindowDays: number | null
  selected: boolean
  highlighted: boolean
  canBulkOps: boolean
  canInventoryWrite: boolean
  /** This row's manual price is being removed. */
  removing: boolean
  actions: InventoryRowActions
}

/**
 * One inventory list row. Memoised on purpose: every prop is a primitive, the product object
 * (stable across refetches by React Query's structural sharing) or the stable `actions`, so a
 * keystroke in the search box, a selection toggle or the back-to-top button appearing re-renders
 * only the rows whose own props changed — it used to re-render every visible row.
 */
export const InventoryProductRow = memo(function InventoryProductRow({
  product,
  index,
  density,
  soldWindowDays,
  selected,
  highlighted,
  canBulkOps,
  canInventoryWrite,
  removing,
  actions,
}: InventoryProductRowProps) {
  const displayName = normalizeDisplayCardName(product.card?.name || product.name || 'Card', product.card?.setNumber)
  return (
    <div className={highlighted ? filterStyles.exactMatch : undefined}>
      <InventoryProductCard
        product={product}
        density={density}
        soldWindowDays={soldWindowDays}
        alternate={index % 2 === 1}
        selected={selected}
        canBulkOps={canBulkOps}
        canInventoryWrite={canInventoryWrite}
        removingPriceId={removing ? product.id : null}
        onToggleSelect={() => actions.toggleSelect(product.id)}
        onImageClick={actions.openImage}
        onEdit={() => actions.edit(product)}
        onSetConsignment={() => actions.setConsignment(product)}
        onPriceHistory={() => actions.priceHistory(product, displayName)}
        onRemoveSale={() => actions.removeSale(product.id, displayName)}
        onRemoveManual={() => actions.removeManual(product.id, displayName)}
        onReport={(effectivePriceCents) => actions.report(product, displayName, effectivePriceCents)}
        onStarterLockedSale={actions.starterLockedSale}
        onStarterLockedManual={actions.starterLockedManual}
      />
    </div>
  )
})
